import { useState, useMemo } from 'react'
import Modal from '../../../components/Modal'
import DateRangeCalendar from '../../../components/DateRangeCalendar'
import { useToast } from '../../../contexts/ToastContext'
import { formatDateRu, fetchAnalyticsForPeriod } from '../utils/analyticsCalculations'
import { exportAnalyticsToExcel } from '../utils/exportExcel'
import { exportAnalyticsToPdf } from '../utils/exportPdf'

const isoDate = (d) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default function ExportAnalyticsModal({
  isOpen,
  onClose,
  activePvz,
  organizationName,
  currentFilterPeriod,
  currentLoadedMetrics,
  useEstimatedRevenue = false,
  forecastAutoCalc = false,
  forecastManualRate = 3.0
}) {
  const toast = useToast()

  const [preset, setPreset] = useState('current_month')
  const [selectionStep, setSelectionStep] = useState('start') // 'start' | 'end'
  const [showCalendar, setShowCalendar] = useState(false)
  const [startDate, setStartDate] = useState(() => {
    const now = new Date()
    return isoDate(new Date(now.getFullYear(), now.getMonth(), 1))
  })
  const [endDate, setEndDate] = useState(() => {
    const now = new Date()
    return isoDate(new Date(now.getFullYear(), now.getMonth() + 1, 0))
  })

  const [format, setFormat] = useState('pdf') // 'pdf' | 'excel'
  const [isGenerating, setIsGenerating] = useState(false)
  const [validationError, setValidationError] = useState(null)

  const handlePresetChange = (newPreset) => {
    setPreset(newPreset)
    setValidationError(null)
    const now = new Date()
    const y = now.getFullYear()
    const m = now.getMonth()

    if (newPreset === 'last_7_days') {
      const d = new Date(now)
      d.setDate(d.getDate() - 6)
      setStartDate(isoDate(d))
      setEndDate(isoDate(now))
      setShowCalendar(false)
    } else if (newPreset === 'current_month') {
      setStartDate(isoDate(new Date(y, m, 1)))
      setEndDate(isoDate(new Date(y, m + 1, 0)))
      setShowCalendar(false)
    } else if (newPreset === 'prev_month') {
      setStartDate(isoDate(new Date(y, m - 1, 1)))
      setEndDate(isoDate(new Date(y, m, 0)))
      setShowCalendar(false)
    } else if (newPreset === 'custom') {
      setShowCalendar(true)
      setSelectionStep('start')
    }
  }

  const handleDateSelect = (dateStr) => {
    setPreset('custom')
    setValidationError(null)
    if (selectionStep === 'start') {
      setStartDate(dateStr)
      if (endDate && dateStr > endDate) {
        setEndDate('')
        setSelectionStep('end')
      } else {
        setSelectionStep('end')
      }
    } else {
      if (startDate && dateStr < startDate) {
        setStartDate(dateStr)
        setEndDate('')
        setSelectionStep('end')
      } else {
        setEndDate(dateStr)
        setSelectionStep('start')
      }
    }
  }

  const humanReadableRange = useMemo(() => {
    if (!startDate || !endDate) return 'Период не выбран'
    return `${formatDateRu(startDate)} — ${formatDateRu(endDate)}`
  }, [startDate, endDate])

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setValidationError('Пожалуйста, укажите обе даты периода')
      return
    }
    if (startDate > endDate) {
      setValidationError('Дата «С» не может быть позже даты «По»')
      return
    }

    setValidationError(null)
    setIsGenerating(true)

    try {
      let metricsToUse = null
      let rawDataToUse = {}

      // Если период совпадает с тем, что уже загружен на экране — переиспользуем вычисленные данные
      if (
        currentFilterPeriod &&
        currentFilterPeriod.startDate === startDate &&
        currentFilterPeriod.endDate === endDate &&
        currentLoadedMetrics
      ) {
        metricsToUse = currentLoadedMetrics
      } else {
        // Подтягиваем данные за выбранный произвольный период
        const res = await fetchAnalyticsForPeriod({
          activePvz,
          filterStartDate: startDate,
          filterEndDate: endDate,
          useEstimatedRevenue,
          forecastAutoCalc,
          forecastManualRate
        })
        metricsToUse = res.metrics
        rawDataToUse = res.rawData
      }

      // Проверка на пустой период
      if (!metricsToUse || (!metricsToUse.hasAnyData && metricsToUse.totalIncome === 0 && metricsToUse.totalExpensesAll === 0)) {
        toast.error('За выбранный период данных нет.')
        setIsGenerating(false)
        return
      }

      const period = { startDate, endDate }

      if (format === 'pdf') {
        await exportAnalyticsToPdf({
          metrics: metricsToUse,
          period,
          activePvz,
          organizationName
        })
        toast.success('Отчёт в формате PDF успешно сформирован и скачан!')
      } else {
        exportAnalyticsToExcel({
          metrics: metricsToUse,
          period,
          activePvz,
          organizationName,
          rawData: rawDataToUse
        })
        toast.success('Таблица в формате Excel (.xlsx) успешно сформирована и скачана!')
      }

      onClose()
    } catch (err) {
      console.error('[ExportAnalyticsModal] Generation error:', err)
      toast.error('Не удалось сформировать отчёт: ' + (err.message || 'попробуйте выбрать другой период'))
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Экспорт аналитики">
      <div className="analytics-export-modal-body">
        <p className="analytics-export-subtitle">Выберите период и формат отчёта</p>

        {/* 1. Пресеты периода */}
        <div className="export-section">
          <label className="export-section-label">Период отчёта</label>
          <div className="export-presets-grid">
            <button
              type="button"
              className={`export-preset-btn ${preset === 'last_7_days' ? 'active' : ''}`}
              onClick={() => handlePresetChange('last_7_days')}
            >
              Последние 7 дней
            </button>
            <button
              type="button"
              className={`export-preset-btn ${preset === 'current_month' ? 'active' : ''}`}
              onClick={() => handlePresetChange('current_month')}
            >
              Текущий месяц
            </button>
            <button
              type="button"
              className={`export-preset-btn ${preset === 'prev_month' ? 'active' : ''}`}
              onClick={() => handlePresetChange('prev_month')}
            >
              Прошлый месяц
            </button>
            <button
              type="button"
              className={`export-preset-btn ${preset === 'custom' ? 'active' : ''}`}
              onClick={() => setPreset('custom')}
            >
              Произвольный
            </button>
          </div>

          {/* Плашки выбранных дат с интерактивным выбором */}
          <div className="export-date-cards-row">
            <div
              className={`export-date-card ${selectionStep === 'start' && showCalendar ? 'active-step' : ''}`}
              onClick={() => {
                setSelectionStep('start')
                setShowCalendar(true)
                setPreset('custom')
              }}
            >
              <div className="export-date-card-title">С даты</div>
              <div className="export-date-card-val">
                {startDate ? formatDateRu(startDate) : 'Выберите'}
              </div>
            </div>

            <div className="export-date-separator">→</div>

            <div
              className={`export-date-card ${selectionStep === 'end' && showCalendar ? 'active-step' : ''}`}
              onClick={() => {
                setSelectionStep('end')
                setShowCalendar(true)
                setPreset('custom')
              }}
            >
              <div className="export-date-card-title">По дату</div>
              <div className="export-date-card-val">
                {endDate ? formatDateRu(endDate) : 'Выберите'}
              </div>
            </div>
          </div>

          {/* Фирменный встроенный календарь при выборе произвольного периода */}
          {showCalendar && (
            <div className="export-calendar-wrapper">
              <div className="export-calendar-hint">
                {selectionStep === 'start' ? 'Выберите дату начала периода' : 'Выберите дату окончания периода'}
              </div>
              <DateRangeCalendar
                startDate={startDate}
                endDate={endDate}
                onDateSelect={handleDateSelect}
              />
            </div>
          )}

          <div className="export-selected-range">
            Выбранный период: <strong>{humanReadableRange}</strong>
          </div>

          {validationError && (
            <div className="export-validation-error">{validationError}</div>
          )}
        </div>

        {/* 2. Выбор формата */}
        <div className="export-section">
          <label className="export-section-label">Формат файла</label>
          <div className="export-formats-grid">
            
            {/* Карточка PDF */}
            <div
              className={`export-format-card ${format === 'pdf' ? 'selected' : ''}`}
              onClick={() => setFormat('pdf')}
            >
              <div className="format-icon-wrapper pdf-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <path d="M10 12v6"/>
                  <path d="M7 15h6"/>
                </svg>
              </div>
              <div className="format-info">
                <div className="format-title">
                  PDF-документ
                  {format === 'pdf' && <span className="format-check">✓</span>}
                </div>
                <div className="format-desc">Красивый отчёт для просмотра, печати и отправки руководству</div>
              </div>
            </div>

            {/* Карточка Excel */}
            <div
              className={`export-format-card ${format === 'excel' ? 'selected' : ''}`}
              onClick={() => setFormat('excel')}
            >
              <div className="format-icon-wrapper excel-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <path d="M8 13h8"/>
                  <path d="M8 17h8"/>
                  <path d="M10 9h4"/>
                </svg>
              </div>
              <div className="format-info">
                <div className="format-title">
                  Excel (.xlsx)
                  {format === 'excel' && <span className="format-check">✓</span>}
                </div>
                <div className="format-desc">Таблицы для дальнейшей работы с данными и формулами</div>
              </div>
            </div>

          </div>
        </div>

        {/* 3. Кнопка «Сформировать отчёт» */}
        <div className="export-actions">
          <button
            type="button"
            className="btn btn-primary export-submit-btn"
            onClick={handleGenerate}
            disabled={isGenerating || !!validationError}
          >
            {isGenerating ? (
              <>
                <span className="export-spinner"></span>
                <span>Формируем отчёт…</span>
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <span>Сформировать отчёт</span>
              </>
            )}
          </button>
        </div>

      </div>
    </Modal>
  )
}
